import React, { useEffect, useState } from "react";
import { Form, Button, Checkbox, message } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { MyTimeLine } from "../../components/MyTimeLine";
import { MyColor } from "../../config/myStyle";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import { FormRequiredMark } from "../../components/FormRequiredMark";
import { MyInputPassword } from "../../components/MyInputPassword";
import { authStore, userStore } from "../../stores";
import PageBox from "../../layout/PageBox";
import { useTranslation } from "react-i18next";

const PASSWORD_RULES = [
  {
    key: "password_8_20",
    test: (v: string) => /^.{8,20}$/.test(v),
  },
  {
    key: "password_digit",
    test: (v: string) => /\d/.test(v),
  },
  {
    key: "password_upper",
    test: (v: string) => /[A-Z]/.test(v),
  },
  {
    key: "password_lower",
    test: (v: string) => /[a-z]/.test(v),
  },
];

const RegisterFormB = observer(() => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const location = useLocation();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const termsLastUpdated = t("register_form_b.terms_last_updated");

  const prevData = location.state

  const onFinish = async (values: any) => {
    const agree: boolean = values.agree
    const password: string = values.password
    if (!agree) {
      message.warning(t("register_form_b.please_read_terms"));
      return;
    }
    const {
      salutation,
      englishGivenName,
      englishSurname,
      email,
      phone,
    } = prevData

    authStore.register({
      // chineseName,
      salutation,
      englishGivenName,
      englishSurname,
      email,
      phone,
      // passcode,
      communicationLanguage: userStore.language,
      password,
    })
  };

  const passwordStatus = PASSWORD_RULES.map(rule => rule.test(password));

  useEffect(() => {
    if (authStore.registerSuccess) {
      message.success(t("register_form_b.register_success"));
      navigate("/register/success");
      authStore.resetRegister();
      userStore.getUser()
    }
  }, [authStore.registerSuccess, navigate, t]);

  useEffect(() => {
    if (!prevData) {
      navigate("/register/form-a");
    }
  }, [prevData, navigate]);

  return (
    <PageBox spinning={authStore.registerStatus.loading}>
      <div style={{ padding: 18 }}>
        <div className="text-[25px]" style={{ color: MyColor.blue }}>{t("register_form_b.register")}</div>
        <div className="text-[20px]" style={{ color: MyColor.blue, opacity: 0.8 }}>{t("register_form_b.welcome")}</div>
        <MyTimeLine className="py-[24px]" current={1} steps={2} />
        <div className="p-[18px] pt-[30px]" style={{ backgroundColor: "white", borderRadius: 20 }}>
          <div className="text-[18px] text-[#B2A292]" style={{ marginBottom: 12 }}>
            {t("register_form_b.account_setting")}
          </div>
          <div className="text-[13px] text-[rgba(0,0,0,0.5)] mb-[20px]">
            {t("register_form_b.account_setting_hint")}
          </div>
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError={true}
            requiredMark={FormRequiredMark}
          >
            <Form.Item
              label={t("register_form_b.password")}
              name="password"
              rules={[
                { required: true, message: t("register_form_b.please_enter_password") },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    if (!/^.{8,20}$/.test(value)) return Promise.reject(t("register_form_b.password_8_20"));
                    if (!/\d/.test(value)) return Promise.reject(t("register_form_b.password_digit"));
                    if (!/[A-Z]/.test(value)) return Promise.reject(t("register_form_b.password_upper"));
                    if (!/[a-z]/.test(value)) return Promise.reject(t("register_form_b.password_lower"));
                    return Promise.resolve();
                  }
                },
              ]}
            >
              <MyInputPassword
                placeholder={t("register_form_b.password")}
                iconRender={visible => visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
                onChange={e => setPassword(e.target.value)}
              />
            </Form.Item>
            {/* 密码规则提示 */}
            <div className="mb-[8px] mt-[-10px] space-y-[8px]">
              {PASSWORD_RULES.map((rule, idx) => (
                <div key={rule.key} className="flex items-center text-[13px]" style={{ color: passwordStatus[idx] ? "#8ac185" : "#bdbdbd" }}>
                  <span style={{
                    display: "inline-block",
                    width: 14, height: 14, borderRadius: "50%", border: "1.5px solid #ccc",
                    background: passwordStatus[idx] ? "#8ac185" : "#f5f5f5",
                    marginRight: 7,
                    verticalAlign: "middle"
                  }} />
                  {t(`register_form_b.${rule.key}`)}
                </div>
              ))}
            </div>
            <Form.Item
              label={t("register_form_b.confirm_password")}
              name="confirmPassword"
              dependencies={["password"]}
              rules={[
                { required: true, message: t("register_form_b.please_confirm_password") },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(t("register_form_b.passwords_not_match"));
                  },
                }),
              ]}
            >
              <MyInputPassword
                placeholder={t("register_form_b.confirm_password")}
                iconRender={visible => visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
              />
            </Form.Item>
            <Form.Item name="optOutUpdates" valuePropName="checked" noStyle>
              <Checkbox style={{ fontSize: 14, color: "#666", marginBottom: 20 }}>
                {t("register_form_b.subscribe_news")}
              </Checkbox>
            </Form.Item>
            {/* 服务条款区块 */}
            <div style={{
              border: "2px solid #eee",
              borderRadius: 2,
              padding: "16px 14px",
              marginBottom: 14,
              background: "#FFFFFF"
            }}>
              <div className="text-[17px] font-medium mb-[6px]" style={{ color: MyColor.blue }}>
                {t("register_form_b.terms_title")}
              </div>
              {termsLastUpdated ? (
                <div className="text-[13px] text-[#888] mb-[4px]">{termsLastUpdated}</div>
              ) : null}
              <div className="text-[13px] text-[#555]" dangerouslySetInnerHTML={{ __html: t("register_form_b.terms_content") }} />
            </div>
            {/* 同意协议 */}
            <Form.Item name="agree" valuePropName="checked" noStyle>
              <Checkbox style={{ fontSize: 14, color: "#666", marginBottom: 20 }}>
                {t("register_form_b.agree_text")}
              </Checkbox>
            </Form.Item>
            {/* 提交按钮 */}
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                style={{
                  width: "100%",
                  borderRadius: 20,
                  background: "#BE9E73",
                  height: 44,
                  fontSize: 18,
                  color: "white",
                  letterSpacing: 1,
                  fontWeight: 500,
                }}
              >
                {t("register_form_b.complete")}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </PageBox>
  );
});

export default RegisterFormB;
